import { useCallback, useMemo, useState } from 'react';
import type { ColumnFiltersState, OnChangeFn, SortingState } from '@tanstack/react-table';
import type { OrderBy } from '@/lib/graphql-query-builder';
import { buildColumnFiltersWhere, buildSortingOrder } from '@/lib/table-query';

/** Rows per page every table offers (Component Map › Pagination); a table starts at the first. */
const PAGE_SIZES = [25, 50, 100];

/** Kept for callers that still name it; phones start at 25 rows like every other screen. */
export const MOBILE_PAGE_SIZE = PAGE_SIZES[0];

/**
 * The page size a table starts with: 25, or 50 / 100 when the table asks for one of those (Component
 * Map › Pagination: 25 / 50 / 100 rows per page, the same on every screen). Read once for the initial
 * state; a later pick from the page-size menu is kept.
 */
export const useInitialPageSize = (requestedPageSize: number) => {
  return PAGE_SIZES.includes(requestedPageSize) ? requestedPageSize : PAGE_SIZES[0];
};

interface UseServerTableOptions {
  /** Rows per page (default 10); a phone always starts at `MOBILE_PAGE_SIZE`. */
  pageSize?: number;
  /** The list's own order, used while no column is sorted. */
  defaultOrder?: OrderBy[];
  /** A record the page was linked to (useLinkedRecord): while set, only it is listed, on page one. */
  linkedId?: string;
}

/**
 * Paging, sorting and column filters of an `AdvanceDataTable` whose rows come from the Data Gateway
 * page by page. Sorting and filtering run on the server (`order`, `columnWhere`), never on the current
 * page only, and both go back to the first page. Merge `columnWhere` into the list's own filter with
 * `mergeWhere`, and spread `tableProps` onto the table:
 *
 * ```tsx
 * const table = useServerTable({ defaultOrder: [{ field: 'Title', direction: 'ASC' }] });
 * const { data } = useGetDepartments({
 *   pageNo: table.pagination.pageIndex + 1,
 *   pageSize: table.pagination.pageSize,
 *   filter: mergeWhere(filter, table.columnWhere),
 *   order: table.order,
 * });
 * <AdvanceDataTable {...table.tableProps} pagination={{ ...table.pagination, totalCount }} ... />
 * ```
 */
export const useServerTable = ({
  pageSize = 10,
  defaultOrder,
  linkedId,
}: UseServerTableOptions = {}) => {
  const initialPageSize = useInitialPageSize(pageSize);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: initialPageSize });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const resetPage = useCallback(() => setPagination((prev) => ({ ...prev, pageIndex: 0 })), []);

  const onPaginationChange = useCallback(
    (next: { pageIndex: number; pageSize: number }) => setPagination(next),
    []
  );

  const onSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      setSorting(updater);
      resetPage();
    },
    [resetPage]
  );

  const onColumnFiltersChange: OnChangeFn<ColumnFiltersState> = useCallback(
    (updater) => {
      setColumnFilters(updater);
      resetPage();
    },
    [resetPage]
  );

  const defaultOrderKey = JSON.stringify(defaultOrder ?? []);
  const order = useMemo(
    () => buildSortingOrder(sorting, JSON.parse(defaultOrderKey) as OrderBy[]),
    [sorting, defaultOrderKey]
  );
  const columnWhere = useMemo(() => {
    const where = buildColumnFiltersWhere(columnFilters);
    return linkedId ? { ...where, ItemId: linkedId } : where;
  }, [columnFilters, linkedId]);
  // The page picked before is kept for when the whole list shows again.
  const shownPagination = linkedId ? { ...pagination, pageIndex: 0 } : pagination;

  return {
    pagination: shownPagination,
    setPagination,
    resetPage,
    order,
    columnWhere,
    tableProps: {
      manualPagination: true,
      manualSorting: true,
      manualFiltering: true,
      sorting,
      onSortingChange,
      columnFilters,
      onColumnFiltersChange,
      onPaginationChange,
    },
  };
};
